import { Utils } from "./utils";

// Base Abstract Classes
interface NotationEventJson {
	type: string;
	duration: number;
	staff?: number;
}

abstract class NotationEvent {
	abstract readonly type: string;

	private parentVoice: Voice | null = null;

	public readonly id: string;
	public readonly duration: number;
	public readonly staff: number;

	public constructor(duration: number, staff: number, id?: string) {
		this.id = id ?? Utils.generateGUID();
		this.duration = duration;
		this.staff = staff ?? 1;
	}

	public setParentVoice(parent: Voice): void {
		this.parentVoice = parent;
	}

	public getVoice(): Voice | null {
		return this.parentVoice;
	}

	public getMeasure(): Measure | null {
		return this.parentVoice?.getMeasure() || null;
	}

	public getSection(): Section | null {
		return this.parentVoice?.getMeasure()?.getSection() || null;
	}

	public getPart(): Part | null {
		return this.parentVoice?.getMeasure()?.getSection()?.getPart() || null;
	}

	public getPartList(): PartList | null {
		return this.parentVoice?.getMeasure()?.getSection()?.getPart()?.getPartList() || null;
	}

	public getVexFlowDuration(): { durationType: string, dots: number } {
		// Validation checks
		const voice = this.getVoice();
		if (!voice) {
			throw new Error("NotationEvent must be associated with a Voice explicitly before operating.");
		}

		const measure = voice.getMeasure();
		if (!measure) {
			throw new Error("Voice must be associated with a Measure explicitly before operating.");
		}

		// Calculate total divisions for a quarter note
		let totalDivisions = measure.divisions * (measure.timeSignature.beatType / 4);

		// Define possible durations and their VexFlow equivalents
		const possibleDurations = [
			{ ratio: 4, type: "w", dots: 0 },     // Whole note
			{ ratio: 2, type: "h", dots: 0 },     // Half note
			{ ratio: 1, type: "q", dots: 0 },     // Quarter note
			{ ratio: 0.5, type: "8", dots: 0 },   // Eighth note
			{ ratio: 0.25, type: "16", dots: 0 }, // Sixteenth note
			{ ratio: 0.125, type: "32", dots: 0 } // Thirty-second note
		];

		let duration = this.duration;

		// Handle tuplets if applicable
		if (this instanceof BasicNotationEvent) {
			const tuplet = this.getTuplet();
			if (tuplet != null) {
				duration = tuplet.calculateTupletDivisions(duration);
			}
		}

		// Determine the duration ratio based on divisions
		const durationRatio = duration / totalDivisions;
		const isRest = this instanceof Rest;

		// Iterate over possible durations to find the best match
		for (const duration of possibleDurations) {
			let calculatedDuration = duration.ratio;
			let dots = 0;

			// Handle dotted durations
			while (calculatedDuration < durationRatio) {
				if (dots >= 2) break; // Limit to double dotted
				dots++;
				calculatedDuration += duration.ratio / (2 ** dots);
			}

			// Check if the duration matches within a small margin of error
			if (Math.abs(calculatedDuration - durationRatio) < 1e-6) {
				const durationType = isRest ? duration.type + "r" : duration.type;
				return { durationType, dots };
			}
		}

		// If no match is found, throw an error
		throw new Error(
			`Could not find a valid VexFlow duration for the given duration: ${this.duration}, totalDivisions: ${totalDivisions}.`
		);
	}

	public abstract createCopy(keepId: boolean): NotationEvent;

	public static fromJson(json: any): NotationEvent {
		switch (json.type) {
			case "tuplet":
				return Tuplet.fromJson(json);
			case "rest":
			case "pitchedNote":
			case "unpitchedNote":
			case "chord":
				return BasicNotationEvent.fromJson(json);
			default:
				throw new Error(`Unknown NotationEvent type: ${json.type}`);
		}
	}

	public abstract toJson(): NotationEventJson;
}

interface BasicNotationEventJson extends NotationEventJson { }

abstract class BasicNotationEvent extends NotationEvent {
	private parentTuplet: Tuplet | null = null;

	public constructor(duration: number, staff: number, id?: string) {
		super(duration, staff, id);
	}

	public setParentTuplet(parentTuplet: Tuplet): void {
		this.parentTuplet = parentTuplet;
	}

	public getTuplet(): Tuplet | null {
		return this.parentTuplet;
	}

	public abstract createCopy(keepId: boolean): BasicNotationEvent;

	public static fromJson(json: any): BasicNotationEvent {
		switch (json.type) {
			case "rest":
				return Rest.fromJson(json);
			case "chord":
			case "pitchedNote":
			case "unpitchedNote":
				return SoundingNotationEvent.fromJson(json);
			default:
				throw new Error(`Unknown BasicNotationEvent type: ${json.type}`);
		}
	}

	public abstract toJson(): BasicNotationEventJson;
}

interface SoundingNotationEventJson extends BasicNotationEventJson {
	lyric: LyricJson | null;
	articulations: ArticulationJson[];
}

abstract class SoundingNotationEvent extends BasicNotationEvent {
	public readonly lyric: Lyric | null;
	protected readonly articulations: Articulation[];

	public constructor(duration: number, lyric: Lyric | null, articulations: Articulation[], staff: number, id?: string) {
		super(duration, staff, id);
		this.lyric = lyric;
		this.articulations = articulations;
	}

	public abstract getArticulations(): Articulation[];

	public static fromJson(json: any): SoundingNotationEvent {
		switch (json.type) {
			case "chord":
				return Chord.fromJson(json);
			case "pitchedNote":
			case "unpitchedNote":
				return Note.fromJson(json);
			default:
				throw new Error(`Unknown SoundingNotationEvent type: ${json.type}`);
		}
	}

	public abstract toJson(): SoundingNotationEventJson;
}

interface NoteJson extends SoundingNotationEventJson {
	tie: TieJson | null;
}

abstract class Note extends SoundingNotationEvent {
	private parentChord: Chord | null = null;

	public readonly tie: Tie | null;

	public constructor(duration: number, tie: Tie | null, lyric: Lyric | null, articualtions: Articulation[], staff: number, id?: string) {
		super(duration, lyric, articualtions, staff, id);
		this.tie = tie;
	}

	public abstract createCopy(keepId: boolean): Note;

	public setParentChord(parentChord: Chord): void {
		this.parentChord = parentChord;
	}

	public getChord(): Chord | null {
		return this.parentChord;
	}

	public getArticulations(): Articulation[] {
		if (this.parentChord != null) {
			return this.articulations.concat(this.parentChord.getArticulations())
		}
		return this.articulations;
	}

	public getNoteName(): string {
		if (this instanceof PitchedNote) {
			return (this as PitchedNote).getNoteName();
		} else if (this instanceof UnpitchedNote) {
			return (this as UnpitchedNote).getNoteName();
		}
		throw new Error("Unsupported Note type.");
	}

	public getVexFlowNoteName(): string {
		if (this instanceof PitchedNote) {
			return (this as PitchedNote).getVexFlowNoteName();
		} else if (this instanceof UnpitchedNote) {
			return (this as UnpitchedNote).getVexFlowNoteName();
		}
		throw new Error("Unsupported Note type.");
	}

	public getVexFlowAccidental(): undefined | "bb" | "b" | "#" | "##" {
		if (this instanceof PitchedNote) {
			return (this as PitchedNote).pitch.getVexFlowAccidental();
		} else if (this instanceof UnpitchedNote) {
			return (this as UnpitchedNote).unpitchedInstrument.displayPitch.getVexFlowAccidental();
		}
		throw new Error("Unsupported Note type.");
	}

	public abstract toMidiNote(startBeat: number): MidiNote;

	public static fromJson(json: any): Note {
		switch (json.type) {
			case "pitchedNote":
				return PitchedNote.fromJson(json);
			case "unpitchedNote":
				return UnpitchedNote.fromJson(json);
			default:
				throw new Error(`Unknown Note type: ${json.type}`);
		}
	}

	public abstract toJson(): NoteJson;
}

// Concrete Classes
interface TupletJson extends NotationEventJson {
	duration: number;
	actualNotes: number;
	normalNotes: number;
	notationEvents: BasicNotationEventJson[];
}

export class Tuplet extends NotationEvent {
	public readonly type = "tuplet";
	public readonly actualNotes: number;
	public readonly normalNotes: number;
	public readonly notationEvents: BasicNotationEvent[];

	public constructor(duration: number, actualNotes: number, normalNotes: number, notationEvents: BasicNotationEvent[], staff: number, id?: string) {
		super(duration, staff, id);
		this.actualNotes = actualNotes;
		this.normalNotes = normalNotes;
		this.notationEvents = notationEvents;
		this.notationEvents.forEach(event => {
			// If the event is a chord, set the parent tuplet for each note in the chord
			if (event instanceof Chord) {
				event.notes.forEach(note => note.setParentTuplet(this));
			}
			event.setParentTuplet(this);
		});
	}

	public setParentVoice(voice: Voice): void {
		super.setParentVoice(voice);
		this.notationEvents.forEach(event => event.setParentVoice(voice));
	}

	public createCopy(keepId: boolean): Tuplet {
		const newNotationEvents = this.notationEvents.map(notationEvent => notationEvent.createCopy(keepId));
		const newTuplet = new Tuplet(this.duration, this.actualNotes, this.normalNotes, newNotationEvents, this.staff, keepId ? this.id : undefined);
		newTuplet.notationEvents.forEach(event => event.setParentTuplet(newTuplet));
		return newTuplet;
	}

	public calculateTupletDivisions(totalDivisions: number): number {
		return (this.actualNotes / this.normalNotes) * totalDivisions;
	}

	public static fromJson(json: any): Tuplet {
		const notationEvents = json.notationEvents.map((event: any) => BasicNotationEvent.fromJson(event));
		const tuplet = new Tuplet(json.duration, json.actualNotes, json.normalNotes, notationEvents, json.staff);
		return tuplet;
	}

	public toJson(): TupletJson {
		return {
			type: this.type,
			duration: this.duration,
			staff: this.staff,
			actualNotes: this.actualNotes,
			normalNotes: this.normalNotes,
			notationEvents: this.notationEvents.map(event => event.toJson())
		};
	}
}

interface RestJson extends BasicNotationEventJson { }

export class Rest extends BasicNotationEvent {
	public readonly type = "rest";

	public constructor(duration: number, staff: number) {
		super(duration, staff);
	}

	public createCopy(): Rest {
		return new Rest(this.duration, this.staff);
	}

	public static fromJson(json: any): Rest {
		return new Rest(json.duration, json.staff);
	}

	public toJson(): RestJson {
		return {
			type: this.type,
			duration: this.duration,
			staff: this.staff
		};
	}
}

interface ChordJson extends SoundingNotationEventJson {
	notes: NoteJson[];
}

export class Chord extends SoundingNotationEvent {
	public readonly type = "chord";
	public readonly notes: Note[];

	public constructor(duration: number, notes: Note[], lyric: Lyric | null, articualtions: Articulation[], staff: number, id?: string) {
		super(duration, lyric, articualtions, staff, id);
		this.notes = notes;
		this.notes.forEach(note => note.setParentChord(this));
	}

	public setParentVoice(voice: Voice): void {
		super.setParentVoice(voice);
		this.notes.forEach(note => note.setParentVoice(voice));
	}

	public createCopy(keepId: boolean): Chord {
		const newNotes = this.notes.map(note => (note as Note).createCopy(keepId));
		return new Chord(
			this.duration,
			newNotes,
			this.lyric ? new Lyric(this.lyric.syllabic, this.lyric.text) : null,
			this.articulations.map(articulation => new Articulation(articulation.type)),
			this.staff,
			keepId ? this.id : undefined
		);
	}

	public getArticulations(): Articulation[] {
		return this.articulations;
	}

	public static fromJson(json: any): Chord {
		const notes = json.notes.map((note: any) => Note.fromJson(note));
		const chord = new Chord(
			json.duration,
			notes,
			json.lyric ? Lyric.fromJson(json.lyric) : null,
			json.articulations.map((articulation: any) => new Articulation(articulation.type)),
			json.staff
		);
		return chord;
	}

	public toJson(): ChordJson {
		return {
			type: this.type,
			duration: this.duration,
			staff: this.staff,
			lyric: this.lyric ? this.lyric.toJson() : null,
			notes: this.notes.map(note => note.toJson()),
			articulations: this.articulations.map(articulation => articulation.toJson())
		};
	}
}

interface PitchedNoteJson extends NoteJson {
	pitch: PitchJson;
}

export class PitchedNote extends Note {
	public readonly type = "pitchedNote";
	public readonly pitch: Pitch;

	public constructor(duration: number, pitch: Pitch, tie: Tie | null, lyric: Lyric | null, articulations: Articulation[], staff: number, id?: string) {
		super(duration, tie, lyric, articulations, staff, id);
		this.pitch = pitch;
	}

	public createCopy(keepId: boolean): PitchedNote {
		return new PitchedNote(
			this.duration,
			new Pitch(this.pitch.getStep(), this.pitch.getAlter(), this.pitch.getOctave()),
			this.tie ? new Tie(this.tie.type) : null,
			this.lyric ? new Lyric(this.lyric.syllabic, this.lyric.text) : null,
			this.articulations.map(articulation => new Articulation(articulation.type)),
			this.staff,
			keepId ? this.id : undefined
		);
	}

	public toMidiNote(startBeat: number): MidiNote {
		const measure = this.getMeasure();
		if (!measure) {
			throw new Error("PitchedNote must be associated with a Measure explicitly before calculating midi notes.");
		}
		const beatDuration = this.duration / measure.divisions || 0;
		const midiNote: MidiNote = new MidiNote(
			this.id,
			this.pitch.getMidiPitch(),
			startBeat,
			beatDuration,
			this.getNoteName(),
			this.getArticulations().map(articulation => articulation.type).includes(Articulation.STACCATO),
			this.getMeasure()?.number || 0,
		);
		return midiNote;
	}

	public getNoteName(): string {
		return this.pitch.getNoteName();
	}

	public getVexFlowNoteName(): string {
		const noteName = this.getNoteName();
		return noteName.slice(0, noteName.length - 1) + "/" + noteName[noteName.length - 1];
	}

	public transpose(oldKey: Key, newKey: Key): void {
		this.pitch.transpose(oldKey, newKey);
	}

	public static fromJson(json: any): PitchedNote {
		return new PitchedNote(
			json.duration,
			Pitch.fromJson(json.pitch),
			json.tie ? Tie.fromJson(json.tie) : null,
			json.lyric ? Lyric.fromJson(json.lyric) : null,
			json.articulations.map((articulation: any) => new Articulation(articulation.type)),
			json.staff
		);
	}

	public toJson(): PitchedNoteJson {
		return {
			type: this.type,
			duration: this.duration,
			staff: this.staff,
			tie: this.tie ? this.tie.toJson() : null,
			lyric: this.lyric ? this.lyric.toJson() : null,
			pitch: {
				step: this.pitch.getStep(),
				alter: this.pitch.getAlter(),
				octave: this.pitch.getOctave()
			},
			articulations: this.articulations.map(articulation => articulation.toJson())
		};
	}
}

interface UnpitchedNoteJson extends NoteJson {
	unpitchedInstrument: UnpitchedInstrumentJson;
}

export class UnpitchedNote extends Note {
	public readonly type = "unpitchedNote";
	public readonly unpitchedInstrument: UnpitchedInstrument;

	public constructor(duration: number, unpitchedInstrument: UnpitchedInstrument, tie: Tie | null, lyric: Lyric | null, articulations: Articulation[], staff: number, id?: string) {
		super(duration, tie, lyric, articulations, staff, id);
		this.unpitchedInstrument = unpitchedInstrument;
	}

	public createCopy(keepId: boolean): UnpitchedNote {
		return new UnpitchedNote(
			this.duration,
			this.unpitchedInstrument.createCopy(),
			this.tie ? new Tie(this.tie.type) : null,
			this.lyric ? new Lyric(this.lyric.syllabic, this.lyric.text) : null,
			this.articulations.map(articulation => new Articulation(articulation.type)),
			this.staff,
			keepId ? this.id : undefined
		);
	}

	public getNoteName(): string {
		return this.unpitchedInstrument.displayPitch.getNoteName();
	}

	public getVexFlowNoteName(): string {
		const noteName = this.getNoteName();
		let vexFlowNoteHead = this.unpitchedInstrument.getVexFlowNoteHeadType();

		// Some note heads require a duration modifier
		if (vexFlowNoteHead === "/X" || vexFlowNoteHead === "/D" || vexFlowNoteHead === "/T") {
			const durationType = this.getVexFlowDuration().durationType;
			const durationModifier = durationType === "w" ? "0" : durationType === "h" ? "1" : "2";
			vexFlowNoteHead += durationModifier;
		}

		return `${noteName.slice(0, noteName.length - 1)}/${noteName[noteName.length - 1]}${vexFlowNoteHead}`;
	}

	public toMidiNote(startBeat: number): MidiNote {
		const measure = this.getMeasure();
		if (!measure) {
			throw new Error("PitchedNote must be associated with a Measure explicitly before calculating midi notes.");
		}
		const beatDuration = this.duration / measure.divisions || 0;
		const midiNote: MidiNote = new MidiNote(
			this.id,
			this.unpitchedInstrument.midiUnpitched,
			startBeat,
			beatDuration,
			this.getNoteName(),
			this.getArticulations().map(articulation => articulation.type).includes(Articulation.STACCATO),
			this.getMeasure()?.number || 0
		);
		return midiNote;
	}

	public static fromJson(json: any): UnpitchedNote {
		return new UnpitchedNote(
			json.duration,
			UnpitchedInstrument.fromJson(json.unpitchedInstrument),
			json.tie ? Tie.fromJson(json.tie) : null,
			json.lyric ? Lyric.fromJson(json.lyric) : null,
			json.articulations.map((articulation: any) => new Articulation(articulation.type)),
			json.staff
		);
	}

	public toJson(): UnpitchedNoteJson {
		return {
			type: this.type,
			duration: this.duration,
			staff: this.staff,
			tie: this.tie ? this.tie.toJson() : null,
			lyric: this.lyric ? this.lyric.toJson() : null,
			unpitchedInstrument: this.unpitchedInstrument,
			articulations: this.articulations.map(articulation => articulation.toJson())
		};
	}
}

// Hierarchy Classes
interface VoiceJson {
	notationEvents: NotationEventJson[];
}

export class Voice {
	public readonly id: string;
	public readonly notationEvents: NotationEvent[];
	private parentMeasure: Measure | null = null;

	public constructor(notationEvents: NotationEvent[], id?: string) {
		this.id = id ?? Utils.generateGUID();
		this.notationEvents = notationEvents;
		this.notationEvents.forEach(event => event.setParentVoice(this));
	}

	public setParentMeasure(measure: Measure): void {
		this.parentMeasure = measure;
	}

	public getMeasure(): Measure | null {
		return this.parentMeasure;
	}

	public getSection(): Section | null {
		return this.parentMeasure?.getSection() || null;
	}

	public getPart(): Part | null {
		return this.parentMeasure?.getPart() || null;
	}

	public getPartList(): PartList | null {
		return this.parentMeasure?.getPartList() || null;
	}

	public transpose(oldKey: Key, newKey: Key): void {
		this.notationEvents.forEach(event => { // Unpitched notes and rests are not transposed
			if (event instanceof PitchedNote) {
				event.transpose(oldKey, newKey);
			} else if (event instanceof Chord) {
				event.notes.forEach(note => {
					if (note instanceof PitchedNote) {
						note.transpose(oldKey, newKey);
					}
				});
			} else if (event instanceof Tuplet) {
				event.notationEvents.forEach(innerEvent => {
					if (innerEvent instanceof PitchedNote) {
						innerEvent.transpose(oldKey, newKey);
					} else if (innerEvent instanceof Chord) {
						innerEvent.notes.forEach(note => {
							if (note instanceof PitchedNote) {
								note.transpose(oldKey, newKey);
							}
						});
					}
				});
			}
		});
	}

	public createCopy(keepId: boolean): Voice {
		const newNotationEvents = this.notationEvents.map(notationEvent => notationEvent.createCopy(keepId));
		return new Voice(newNotationEvents, keepId ? this.id : undefined);
	}

	public static fromJson(json: any): Voice {
		const voice = new Voice(json.notationEvents.map((notationEvent: any) => NotationEvent.fromJson(notationEvent)));
		return voice;
	}

	public toJson(): VoiceJson {
		return {
			notationEvents: this.notationEvents.map(event => event.toJson())
		};
	}
}

interface MeasureJson {
	key: KeyJson;
	timeSignature: TimeSignatureJson;
	number: number;
	divisions: number;
	voices: VoiceJson[];
}

export class Measure {
	public readonly id: string;
	public readonly voices: Voice[];
	public readonly timeSignature: TimeSignature;
	public readonly number: number;
	public readonly divisions: number;
	private parentSection: Section | null = null;

	private key: Key;

	public constructor(key: Key, time: TimeSignature, number: number, divisions: number, voices: Voice[], id?: string) {
		this.id = id ?? Utils.generateGUID();
		this.voices = voices;
		this.voices.forEach(voice => voice.setParentMeasure(this));
		this.key = key;
		this.timeSignature = time;
		this.number = number;
		this.divisions = divisions;
	}

	public getNotationEvents(): NotationEvent[] {
		return this.voices.map(voice => voice.notationEvents).flat();
	}

	public setParentSection(section: Section): void {
		this.parentSection = section;
	}

	public getSection(): Section | null {
		return this.parentSection;
	}

	public getPart(): Part | null {
		return this.parentSection?.getPart() || null;
	}

	public getPartList(): PartList | null {
		return this.parentSection?.getPartList() || null;
	}

	public getKey(): Key {
		return this.key;
	}

	public transpose(newKey: Key): void {
		this.voices.forEach(voice => voice.transpose(this.key, newKey));
		this.key = newKey;
	}

	// Shift notes by a set number of semitones
	public shiftNotes(steps: number): void {
		this.voices.forEach(voice => {
			voice.notationEvents.forEach(event => {
				if (event instanceof PitchedNote) {
					event.pitch.shift(steps, this.key);
				} else if (event instanceof Chord) {
					event.notes.forEach(note => {
						if (note instanceof PitchedNote) {
							note.pitch.shift(steps, this.key);
						}
					});
				} else if (event instanceof Tuplet) {
					event.notationEvents.forEach(innerEvent => {
						if (innerEvent instanceof PitchedNote) {
							innerEvent.pitch.shift(steps, this.key);
						} else if (innerEvent instanceof Chord) {
							innerEvent.notes.forEach(note => {
								if (note instanceof PitchedNote) {
									note.pitch.shift(steps, this.key);
								}
							});
						}
					});
				}
			});
		});
	}

	// Shift notes into a specified range by adjusting their pitch one by one up or down an octave
	public shiftNotesIntoRange(lowestNote: number, highestNote: number): void {
		this.voices.forEach(voice => {
			voice.notationEvents.forEach(event => {
				if (event instanceof PitchedNote) {
					if (event.pitch.getMidiPitch() < lowestNote) {
						event.pitch.shift(12, this.key);
					} else if (event.pitch.getMidiPitch() > highestNote) {
						event.pitch.shift(-12, this.key);
					}
				} else if (event instanceof Chord) {
					event.notes.forEach(note => {
						if (note instanceof PitchedNote) {
							if (note.pitch.getMidiPitch() < lowestNote) {
								note.pitch.shift(12, this.key);
							} else if (note.pitch.getMidiPitch() > highestNote) {
								note.pitch.shift(-12, this.key);
							}
						}
					});
				} else if (event instanceof Tuplet) {
					event.notationEvents.forEach(innerEvent => {
						if (innerEvent instanceof PitchedNote) {
							if (innerEvent.pitch.getMidiPitch() < lowestNote) {
								innerEvent.pitch.shift(12, this.key);
							} else if (innerEvent.pitch.getMidiPitch() > highestNote) {
								innerEvent.pitch.shift(-12, this.key);
							}
						} else if (innerEvent instanceof Chord) {
							innerEvent.notes.forEach(note => {
								if (note instanceof PitchedNote) {
									if (note.pitch.getMidiPitch() < lowestNote) {
										note.pitch.shift(12, this.key);
									} else if (note.pitch.getMidiPitch() > highestNote) {
										note.pitch.shift(-12, this.key);
									}
								}
							});
						}
					});
				}
			});
		});
	}

	public createCopy(keepId: boolean): Measure {
		const newVoices = this.voices.map(voice => voice.createCopy(keepId));
		return new Measure(this.key, this.timeSignature, this.number, this.divisions, newVoices, keepId ? this.id : undefined);
	}

	public static fromJson(json: any): Measure {
		const measure = new Measure(
			Key.fromJson(json.key),
			TimeSignature.fromJson(json.timeSignature),
			json.number,
			json.divisions,
			json.voices.map((voice: any) => Voice.fromJson(voice))
		);
		return measure;
	}

	public toJson(): MeasureJson {
		return {
			key: this.key.toJson(),
			timeSignature: this.timeSignature.toJson(),
			number: this.number,
			divisions: this.divisions,
			voices: this.voices.map(voice => voice.toJson())
		};
	}
}

export interface SectionJson {
	name: string;
	measures: MeasureJson[];
}

export class Section {
	public readonly id: string;
	public readonly name: string;
	private measures: Measure[];
	private parentPart: Part | null = null;

	public constructor(name: string, measures: Measure[], id?: string) {
		this.id = id ?? Utils.generateGUID();
		this.name = name;
		this.measures = measures;
		this.measures.forEach(measure => measure.setParentSection(this));
	}

	public transpose(newKey: Key): void {
		this.measures.forEach(measure => measure.transpose(newKey));
	}

	public shiftNotes(steps: number): void {
		this.measures.forEach(measure => measure.shiftNotes(steps));
	}

	public shiftNotesIntoRange(lowestNote: number, highestNote: number): void {
		this.measures.forEach(measure => measure.shiftNotesIntoRange(lowestNote, highestNote));
	}

	public fitToInstrument(instrument: Instrument): void {
		let highestNoteInSection: number | null = null;
		let lowestNoteInSection: number | null = null;
		this.measures.forEach(measure => {
			measure.voices.forEach(voice => {
				voice.notationEvents.forEach(event => {
					if (event instanceof PitchedNote) {
						const pitch = event.pitch.getMidiPitch();
						if (highestNoteInSection === null || pitch > highestNoteInSection) {
							highestNoteInSection = pitch;
						}
						if (lowestNoteInSection === null || pitch < lowestNoteInSection) {
							lowestNoteInSection = pitch;
						}
					} else if (event instanceof Chord) {
						event.notes.forEach(note => {
							if (note instanceof PitchedNote) {
								const pitch = note.pitch.getMidiPitch();
								if (highestNoteInSection === null || pitch > highestNoteInSection) {
									highestNoteInSection = pitch;
								}
								if (lowestNoteInSection === null || pitch < lowestNoteInSection) {
									lowestNoteInSection = pitch;
								}
							}
						});
					}
				});
			});
		});
		const shouldTransposeDownOctave = highestNoteInSection !== null && highestNoteInSection > instrument.highestPossibleNote;
		const canTransposeDownOctave = lowestNoteInSection !== null && lowestNoteInSection >= instrument.lowestPossibleNote + 12;
		const shouldTransposeUpOctave = lowestNoteInSection !== null && lowestNoteInSection < instrument.lowestPossibleNote;
		const canTransposeUpOctave = highestNoteInSection !== null && highestNoteInSection <= instrument.highestPossibleNote - 12;

		if (shouldTransposeDownOctave && canTransposeDownOctave) {
			this.shiftNotes(-12);
		} else if (shouldTransposeUpOctave && canTransposeUpOctave) {
			this.shiftNotes(12);
		} else {
			this.shiftNotesIntoRange(instrument.lowestPossibleNote, instrument.highestPossibleNote);
		}
	}

	private hash: string | null = null;
	public getHash(): string {
		if (this.hash === null) {
			this.hash = this.measures.map(measure => {
				return measure.voices.map(voice => {
					return voice.notationEvents.map(event => {
						return JSON.stringify(event.toJson());
					}).join("|");
				}).join("|");
			}).join("|");
		}
		return this.hash;
	}

	public getNotationEvents(): NotationEvent[] {
		return this.measures.map(measure => measure.getNotationEvents()).flat();
	}

	public getNoteIdSet(startMeasure: number, endMeasure: number): Set<string> {
		const noteIds = new Set<string>();
		this.measures.slice(startMeasure, endMeasure).forEach(measure => measure.voices.forEach(voice => {
			voice.notationEvents.forEach(event => {
				if (event instanceof Note) {
					noteIds.add(event.id);
				} else if (event instanceof Chord) {
					event.notes.forEach(note => noteIds.add(note.id));
				} else if (event instanceof Tuplet) {
					event.notationEvents.forEach(innerEvent => {
						if (innerEvent instanceof Note) {
							noteIds.add(innerEvent.id);
						} else if (innerEvent instanceof Chord) {
							innerEvent.notes.forEach(note => noteIds.add(note.id));
						}
					});
				}
			});
		}));

		return noteIds;
	}

	public getMeasures(): Measure[] {
		return [...this.measures];
	}

	public getMeasure(index: number): Measure {
		return this.measures[index];
	}

	public isEmpty(): boolean {
		for (const measure of this.measures) {
			for (const voice of measure.voices) {
				for (const notationEvent of voice.notationEvents) {
					if (notationEvent instanceof SoundingNotationEvent) {
						return false;
					}
				}
			}
		}
		return true;
	}

	public getStartMeasure() {
		return this.measures[0].number;
	}

	public getLength(): number {
		return this.measures.length;
	}

	public getTotalBeats(): number {
		return this.measures.reduce((totalBeats, measure) => totalBeats + measure.timeSignature.beats, 0);
	}

	public toMidiMeasures(trimStart: number = 0, trimEnd: number = 0): MidiMeasure[] {
		const midiMeasures: MidiMeasure[] = [];
		const trimmedMeasures = this.measures.slice(trimStart, this.measures.length - trimEnd);
		const tiedNotesByName = new Map<string, MidiNote>();

		const handleNote = (midiNote: MidiNote, tieType: string | null, notes: MidiNote[]) => {
			if (tieType === "stop" || tieType === "continue") {
				const tiedNote = tiedNotesByName.get(midiNote.noteName);
				if (tiedNote && tiedNote.startBeat + tiedNote.getBeatDuration() == midiNote.startBeat) {
					tiedNote.addBeatDuration(midiNote.getBeatDuration());
				}
				if (tieType === "stop") {
					tiedNotesByName.delete(midiNote.noteName);
				}
			} else {
				notes.push(midiNote);
				if (tieType === "start") {
					tiedNotesByName.set(midiNote.noteName, midiNote);
				}
			}
		};

		const processEvent = (event: NotationEvent, startBeat: number, divisions: number, notes: MidiNote[]): number => {
			if (event instanceof Rest) {
				// Rests are not represented in MIDI
				return startBeat + event.duration / divisions;
			}

			if (event instanceof Note) {
				const midiNote = event.toMidiNote(startBeat);
				handleNote(midiNote, event.tie?.type || null, notes);
				return startBeat + event.duration / divisions;
			} else if (event instanceof Chord) {
				event.notes.forEach(note => {
					const midiNote = note.toMidiNote(startBeat);
					handleNote(midiNote, note.tie?.type || null, notes);
				});
				return startBeat + event.duration / divisions;
			} else if (event instanceof Tuplet) {
				event.notationEvents.forEach(innerEvent => {
					startBeat = processEvent(innerEvent, startBeat, divisions, notes);
				});
				return Math.round(startBeat * 10) / 10; // Round 10 digits to prevent floating point errors
			} else {
				throw new Error(`Unsupported notation event type: ${event.constructor.name}`);
			}
		};

		trimmedMeasures.forEach(measure => {
			const notes: MidiNote[] = [];
			measure.voices.forEach(voice => {
				let startBeat = 0;
				voice.notationEvents.forEach(event => {
					startBeat = processEvent(event, startBeat, measure.divisions, notes);
				});
			});
			midiMeasures.push(new MidiMeasure(measure.timeSignature, notes));
		});

		return midiMeasures;
	}
	
	public setParentPart(parent: Part): void {
		this.parentPart = parent;
	}

	public getPart(): Part | null {
		return this.parentPart;
	}

	public getPartList(): PartList | null {
		return this.parentPart?.getPartList() || null;
	}

	public createCopy(keepId: boolean): Section {
		const newMeasures = this.measures.map(measure => measure.createCopy(keepId));
		return new Section(this.name, newMeasures, keepId ? this.id : undefined);
	}

	public static fromJson(json: any): Section {
		const section = new Section(json.name, json.measures.map((measure: any) => Measure.fromJson(measure)));
		return section;
	}

	public toJson(): SectionJson {
		return {
			name: this.name,
			measures: this.measures.map(measure => measure.toJson())
		};
	}
}

export class Part {
	public readonly id: string;
	public readonly name: string;
	public readonly sections: Section[];
	public readonly instrument: Instrument | null;
	private parentPartList: PartList | null = null;

	public constructor(name: string, sections: Section[]) {
		this.id = Utils.generateGUID();
		this.name = name;
		this.sections = sections;
		this.sections.forEach(section => section.setParentPart(this));
		this.instrument = Instrument.getInstrumentMatch(name);
	}

	public getSectionInfo(): { sectionName: string, sectionLength: number }[] {
		return this.sections.map(section => {
			return {
				sectionName: section.name,
				sectionLength: section.getLength()
			}
		});
	}

	private measureLength: number | undefined;
	public getLength() {
		if (this.measureLength == undefined) {
			// Calculate the length of the longest section in the part list
			this.measureLength = this.sections.reduce((totalLength, section) => totalLength + section.getLength(), 0);
		}
		return this.measureLength;
	}

	public setParentPartList(parent: PartList): void {
		this.parentPartList = parent;
	}

	public getPartList(): PartList | null {
		return this.parentPartList;
	}

	public static fromJson(json: any): Part {
		const part = new Part(json.name, json.sections.map((section: any) => Section.fromJson(section)));
		return part;
	}
}

export class PartList {
	public readonly parts: Part[];
	private name: string | undefined;
	private songId: string | undefined;

	public constructor(parts: Part[]) {
		this.parts = parts.filter(part => part.instrument != null); // Filter out parts that don't have and instrument or have an instrument that the user doesn't have access to
		this.parts.forEach(part => part.setParentPartList(this));
		this.parts.sort((a, b) => (b.instrument?.midiProgram ?? 0) - (a.instrument?.midiProgram ?? 0));
	}

	public setSongId(songId: string): void {
		this.songId = songId;
	}

	public getSongId(): string | undefined {
		return this.songId;
	}

	public setName(name: string): void {
		this.name = name;
	}

	public getName(): string | undefined {
		return this.name;
	}

	public transpose(newKey: any): void {
		this.parts.forEach(part => {
			part.sections.forEach(section => {
				section.transpose(newKey);
				if (part.instrument != null) {
					section.fitToInstrument(part.instrument);
				}
			});
		});
	}

	private measureLength: number | undefined;
	public getLength() {
		if (this.measureLength == undefined) {
			// Calculate the length of the longest section in the part list
			this.measureLength = this.parts.map(part => part.getLength()).reduce((totalLength, partLength) => Math.max(totalLength, partLength), 0)
		}
		return this.measureLength;
	}

	public static fromJson(json: any): PartList {
		const partList = new PartList(json.parts.map((part: any) => Part.fromJson(part)));
		return partList;
	}
}

// Supporting Classes
interface PitchJson {
	step: string;
	alter: number;
	octave: number;
}

export class Pitch {
	private step: string;
	private alter: number;
	private octave: number;

	public constructor(step: string, alter: number, octave: number) {
		this.step = step;
		this.alter = alter;
		this.octave = octave;
	}

	public getStep(): string {
		return this.step;
	}

	public getAlter(): number {
		return this.alter;
	}

	public getOctave(): number {
		return this.octave;
	}

	/**
	 * Returns the MIDI pitch number by combining the octave and pitch class.
	 */
	public getMidiPitch(): number {
		return this.octave * 12 + this.getPitchClass();
	}

	/**
	 * Calculates the pitch class (0-11) of the pitch.
	 * The base pitch classes (without alteration) are as follows:
	 *   C -> 0, D -> 2, E -> 4, F -> 5, G -> 7, A -> 9, B -> 11
	 */
	public getPitchClass(): number {
		let pitchClass = 0;
		switch (this.step) {
			case "C":
				pitchClass = 0;
				break;
			case "D":
				pitchClass = 2;
				break;
			case "E":
				pitchClass = 4;
				break;
			case "F":
				pitchClass = 5;
				break;
			case "G":
				pitchClass = 7;
				break;
			case "A":
				pitchClass = 9;
				break;
			case "B":
				pitchClass = 11;
				break;
			default:
				throw new Error(`Unknown step: ${this.step}`);
		}
		pitchClass += this.alter;
		return pitchClass;
	}

	/**
	 * Returns the note in VexFlow format, e.g., "C#4", where the accidental is determined by getVexFlowAccidental.
	 */
	public getNoteName(): string {
		return `${this.step}${this.getVexFlowAccidental() ?? ""}${this.octave}`;
	}

	/**
	 * Returns the VexFlow accidental string.
	 */
	public getVexFlowAccidental(): "bb" | "b" | "#" | "##" | undefined {
		switch (this.alter) {
			case -2:
				return "bb";
			case -1:
				return "b";
			case 0:
				return undefined;
			case 1:
				return "#";
			case 2:
				return "##";
			default:
				throw new Error(`Invalid alter: ${this.alter}`);
		}
	}

	/**
	 * Transposes this pitch from an old key to a new key.
	 * The old and new key objects are expected to expose:
	 *   - a property "fifths" indicating the key signature,
	 *   - a method calculateChromaticStepsToKey(newKey: Key) that returns the optimal transposition in semitones.
	 *
	 * @param oldKey - The original key.
	 * @param newKey - The target key.
	 */
	public transpose(oldKey: any, newKey: any): void {
		// Calculate the optimal chromatic step difference between keys.
		const chromaticSteps = newKey.calculateChromaticStepsToKey(oldKey);

		// Compute the new MIDI pitch after transposition.
		const currentMidi = this.getMidiPitch();
		const newMidi = currentMidi + chromaticSteps;
		const newOctave = Math.floor(newMidi / 12);
		const newPitchClass = ((newMidi % 12) + 12) % 12;

		// Determine the preferred spelling based on the new key's signature.
		// Typically, keys with negative fifths (more flats) favor flat spelling.
		const preferFlats = newKey.fifths < 0;

		// Convert the pitch class to a step and alter considering the new key.
		const { step, alter } = Pitch.pitchClassToStepAndAlter(newPitchClass, preferFlats);

		// Update the Pitch object.
		this.step = step;
		this.alter = alter;
		this.octave = newOctave;
	}

	/**
	 * Shift the pitch by a specified number of semitones.
	 * Positive values shift up, negative values shift down.
	 */
	public shift(semitones: number, key: Key): void {
		const currentMidi = this.getMidiPitch();
		const newMidi = currentMidi + semitones;
		const newOctave = Math.floor(newMidi / 12);
		const newPitchClass = ((newMidi % 12) + 12) % 12;

		// Determine the preferred spelling based on the key's signature.
		// Typically, keys with negative fifths (more flats) favor flat spelling.
		const preferFlats = key.fifths < 0;

		// Convert the pitch class to a step and alter.
		const { step, alter } = Pitch.pitchClassToStepAndAlter(newPitchClass, preferFlats);

		// Update the Pitch object.
		this.step = step;
		this.alter = alter;
		this.octave = newOctave;
	}

	/**
	 * Converts a pitch class (0-11) into a step and accidental (alter) based on a naming preference.
	 * The parameter "preferFlats" indicates whether to use flat-friendly naming:
	 *
	 * For sharp-friendly naming:
	 *   0 -> C,  1 -> C# (C, +1),  2 -> D,  3 -> D# (D, +1),  4 -> E,
	 *   5 -> F,  6 -> F# (F, +1),  7 -> G,  8 -> G# (G, +1),  9 -> A,
	 *   10 -> A# (A, +1), 11 -> B.
	 *
	 * For flat-friendly naming:
	 *   0 -> C,  1 -> Db (D, -1),  2 -> D,  3 -> Eb (E, -1),  4 -> E,
	 *   5 -> F,  6 -> Gb (G, -1),  7 -> G,  8 -> Ab (A, -1),  9 -> A,
	 *   10 -> Bb (B, -1), 11 -> B.
	 *
	 * @param pitchClass - The pitch class (0-11).
	 * @param preferFlats - Whether to use flat-friendly naming.
	 * @returns An object with the step and alter values.
	 */
	private static pitchClassToStepAndAlter(
		pitchClass: number,
		preferFlats: boolean
	): { step: string; alter: number } {
		if (preferFlats) {
			// Flat-friendly mapping.
			switch (pitchClass) {
				case 0:
					return { step: "C", alter: 0 };
				case 1:
					return { step: "D", alter: -1 }; // Db
				case 2:
					return { step: "D", alter: 0 };
				case 3:
					return { step: "E", alter: -1 }; // Eb
				case 4:
					return { step: "E", alter: 0 };
				case 5:
					return { step: "F", alter: 0 };
				case 6:
					return { step: "G", alter: -1 }; // Gb
				case 7:
					return { step: "G", alter: 0 };
				case 8:
					return { step: "A", alter: -1 }; // Ab
				case 9:
					return { step: "A", alter: 0 };
				case 10:
					return { step: "B", alter: -1 }; // Bb
				case 11:
					return { step: "B", alter: 0 };
				default:
					throw new Error(`Invalid pitch class: ${pitchClass}`);
			}
		} else {
			// Sharp-friendly mapping.
			switch (pitchClass) {
				case 0:
					return { step: "C", alter: 0 };
				case 1:
					return { step: "C", alter: 1 }; // C#
				case 2:
					return { step: "D", alter: 0 };
				case 3:
					return { step: "D", alter: 1 }; // D#
				case 4:
					return { step: "E", alter: 0 };
				case 5:
					return { step: "F", alter: 0 };
				case 6:
					return { step: "F", alter: 1 }; // F#
				case 7:
					return { step: "G", alter: 0 };
				case 8:
					return { step: "G", alter: 1 }; // G#
				case 9:
					return { step: "A", alter: 0 };
				case 10:
					return { step: "A", alter: 1 }; // A#
				case 11:
					return { step: "B", alter: 0 };
				default:
					throw new Error(`Invalid pitch class: ${pitchClass}`);
			}
		}
	}

	/**
	 * Creates a Pitch instance from a JSON object.
	 */
	static fromJson(json: any): Pitch {
		return new Pitch(json.step, json.alter, json.octave);
	}

	/**
	 * Serializes this Pitch instance to a JSON object.
	 */
	public toJson(): { step: string; alter: number; octave: number } {
		return {
			step: this.step,
			alter: this.alter,
			octave: this.octave,
		};
	}
}

interface UnpitchedInstrumentJson {
	name: string;
	midiUnpitched: number;
	noteHead: string;
	displayPitch: Pitch;
}

export class UnpitchedInstrument {
	public readonly name: string;
	public readonly midiUnpitched: number;
	public readonly noteHead: string;
	public readonly displayPitch: Pitch;

	public constructor(name: string, midiUnpitched: number, noteHead: string, displayPitch: Pitch) {
		this.name = name;
		this.midiUnpitched = midiUnpitched;
		this.noteHead = noteHead;
		this.displayPitch = displayPitch;
	}

	public getVexFlowNoteHeadType(): string | undefined {
		switch (this.noteHead) {
			case "normal":
				return ""; // Default VexFlow notehead
			case "triangle":
				return "/T";
			case "diamond":
				return "/D";
			case "x":
				return "/X";
			case "circle-x":
				return "/CX";
			case "back slashed":
				return "/SB";
			default:
				console.warn(`Unsupported notehead: ${this.noteHead}, falling back to normal.`);
				return undefined;
		}
	}

	public createCopy(): UnpitchedInstrument {
		return new UnpitchedInstrument(this.name, this.midiUnpitched, this.noteHead, new Pitch(this.displayPitch.getStep(), this.displayPitch.getAlter(), this.displayPitch.getOctave()));
	}

	public static fromJson(json: any): UnpitchedInstrument {
		return new UnpitchedInstrument(json.name, json.midiUnpitched, json.noteHead, Pitch.fromJson(json.displayPitch));
	}

	public toJson(): UnpitchedInstrumentJson {
		return {
			name: this.name,
			midiUnpitched: this.midiUnpitched,
			noteHead: this.noteHead,
			displayPitch: this.displayPitch
		};
	}
}

interface TieJson {
	type: string;
}

export class Tie {
	public readonly type: "start" | "stop" | "continue";

	public constructor(type: "start" | "stop" | "continue") {
		this.type = type;
	}

	public static fromJson(json: any): Tie {
		return new Tie(json.type);
	}

	public toJson(): TieJson {
		return {
			type: this.type
		};
	}
}

interface LyricJson {
	syllabic: string;
	text: string;
}

export class Lyric {
	public readonly syllabic: "begin" | "middle" | "end" | "single";
	public readonly text: string;

	public constructor(syllabic: "begin" | "middle" | "end" | "single", text: string) {
		this.syllabic = syllabic;
		this.text = text;
	}

	public static fromJson(json: any): Lyric {
		return new Lyric(json.syllabic, json.text);
	}

	public toJson(): LyricJson {
		return {
			syllabic: this.syllabic,
			text: this.text
		};
	}
}

interface ArticulationJson {
	type: string;
}

export class Articulation {
	public static readonly ACCENT = "accent";
	public static readonly STRONG_ACCENT = "strong-accent";
	public static readonly STACCATO = "staccato";
	public static readonly TENUTO = "tenuto";

	public readonly type: "accent" | "strong-accent" | "staccato" | "tenuto";

	public constructor(type: "accent" | "strong-accent" | "staccato" | "tenuto") {
		this.type = type;
	}

	public static fromJson(json: any): Articulation {
		return new Articulation(json.type);
	}

	public toJson(): ArticulationJson {
		return {
			type: this.type
		};
	}
}

interface KeyJson {
	fifths: number;
}

export class Key {
	public readonly fifths: number;

	public constructor(fifths: number) {
		this.fifths = fifths;
	}

	public calculateChromaticStepsToKey(targetKey: Key): number {
		// Calculate the raw difference in semitones.
		const rawDifference = 7 * (this.fifths - targetKey.fifths);

		// Normalize the raw difference to the range [-6, 6].
		// The formula below ensures that the result is the minimal chromatic interval.
		const optimalSteps = (((rawDifference + 6) % 12 + 12) % 12) - 6;

		return optimalSteps;
	}

	public static fromJson(json: any): Key {
		return new Key(json.fifths);
	}

	public toJson(): KeyJson {
		return {
			fifths: this.fifths
		};
	}
}

interface TimeSignatureJson {
	beats: number;
	beatType: number;
}

export class TimeSignature {
	public readonly beats: number;
	public readonly beatType: number;

	constructor(beats: number, beatType: number) {
		this.beats = beats;
		this.beatType = beatType;
	}

	public static fromJson(json: any): TimeSignature {
		return new TimeSignature(json.beats, json.beatType);
	}

	public toJson(): TimeSignatureJson {
		return {
			beats: this.beats,
			beatType: this.beatType
		};
	}
}
export interface TempoJson {
	bpm: number;
}

export class Tempo {
	public static readonly MAX_BPM: number = 130;
	public static readonly MIN_BPM: number = 80;
	public static readonly DEFAULT_TEMPO: Tempo = new Tempo(120);

	private readonly bpm: number;

	public constructor(bpm: number) {
		this.bpm = Math.min(Math.max(bpm, Tempo.MIN_BPM), Tempo.MAX_BPM);
	}

	public getBpm(): number {
		return this.bpm;
	}

	public static fromJson(json: any): Tempo {
		return new Tempo(json?.bpm ?? Tempo.DEFAULT_TEMPO.bpm);
	}

	public toJson(): TempoJson {
		return {
			bpm: this.bpm
		};
	}
}

// MIDI Conversion
export class MidiMeasure {
	public readonly timeSignature: TimeSignature;
	public readonly notes: MidiNote[];

	constructor(timeSignature: TimeSignature, notes: MidiNote[]) {
		this.timeSignature = timeSignature;
		this.notes = notes;
	}
}

export class MidiNote {
	private beatDuration: number;

	public readonly id: string;
	public readonly midi: number;
	public readonly startBeat: number;
	public readonly noteName: string;
	public readonly velocity: number;
	public readonly staccato: boolean;
	public readonly measureNumber: number;

	constructor(id: string, midi: number, startBeat: number, beatDuration: number, noteName: string, staccato: boolean, measureNumber: number) {
		this.id = id;
		this.midi = midi;
		this.startBeat = startBeat; // Relative to the measure
		this.beatDuration = beatDuration;
		this.noteName = noteName;
		this.staccato = staccato;
		this.measureNumber = measureNumber;
		this.velocity = 0.8; // TODO: Make this configurable based on dynamics
	}

	public addBeatDuration(beatDuration: number): void {
		this.beatDuration += beatDuration;
	}

	public getBeatDuration(): number {
		if (this.staccato) {
			return Math.min(this.beatDuration, 0.25);
		}
		return this.beatDuration;
	}
}

// Instrument
export class Instrument {
	private static AllInstrumentOptions: Instrument[];

	public static getAllInstrumentOptions(): Instrument[] {
		return this.AllInstrumentOptions;
	}

	// Creates a non-functional mock instrument with the given name for display purposes
	public static createMockInstrument(name: string): Instrument {
		return new Instrument(
			Utils.generateGUID(),
			name,
			name,
			"SoundFont/" + name,
			"grandstaff",
			[],
			108,
			21,
			127,
			0,
			0
		);
	}

	public readonly id: string;
	public readonly name: string;
	public readonly soundfontInstrument: string;
	public readonly abbreviation: string;
	public readonly clef: "grandstaff" | "treble" | "bass" | "alto" | "tenor" | "percussion";
	public readonly matchers: string[] = [];
	public readonly highestRecommendedNote: number;
	public readonly lowestRecommendedNote: number;
	public readonly highestPossibleNote: number;
	public readonly lowestPossibleNote: number;
	public readonly midiProgram: number;

	private constructor(
		id: string,
		name: string,
		abbreviation: string,
		soundfontInstrument: string,
		clef: "grandstaff" | "treble" | "bass" | "alto" | "tenor" | "percussion",
		matchers: string[],
		highestRecommendedNote: number,
		lowestRecommendedNote: number,
		highestPossibleNote: number,
		lowestPossibleNote: number,
		midiProgram: number
	) {
		this.id = id;
		this.name = name;
		this.abbreviation = abbreviation;
		this.soundfontInstrument = soundfontInstrument;
		this.clef = clef;
		this.matchers = matchers;
		this.highestRecommendedNote = highestRecommendedNote;
		this.lowestRecommendedNote = lowestRecommendedNote;
		this.highestPossibleNote = highestPossibleNote;
		this.lowestPossibleNote = lowestPossibleNote;
		this.midiProgram = midiProgram;
	}

	public static getInstrumentMatch(text: string): Instrument | null {
		if (Instrument.AllInstrumentOptions.length === 0) {
			throw new Error("Instrument options must be loaded before calling getInstrumentMatch.");
		}
		const matchedInstrument = Instrument.AllInstrumentOptions.find(instrument => instrument.matchers.includes(text));
		return matchedInstrument || null;
	}

	public createCopy(): Instrument {
		return new Instrument(
			this.id,
			this.name,
			this.abbreviation,
			this.soundfontInstrument,
			this.clef,
			this.matchers,
			this.highestRecommendedNote,
			this.lowestRecommendedNote,
			this.highestPossibleNote,
			this.lowestPossibleNote,
			this.midiProgram
		);
	}

	public static fromJson(json: any): Instrument {
		return new Instrument(
			json.instrumentId,
			json.instrumentName,
			json.abbreviation,
			json.soundfontInstrumentName,
			json.clef,
			json.matchers,
			json.highestRecommendedNote,
			json.lowestRecommendedNote,
			json.highestPossibleNote,
			json.lowestPossibleNote,
			json.midiProgram
		);
	}
}